/* ============================================================
 * Client-Side Structured Logger for AYU
 * 
 * Usage:
 *   import { createLogger } from "@/lib/logger";
 *   const log = createLogger("RealtimeVoice");
 *   log.info("WebSocket connected", { sessionId });
 *   log.error("Playback failed", { error: err.message });
 *
 * Development: debug, info, warn, error
 * Production: warn, error only (avoid noisy browser console)
 * ============================================================ */

type LogLevel = "debug" | "info" | "warn" | "error";

const LEVEL_ORDER: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

const isProd = import.meta.env.PROD;
const minLevel: LogLevel = isProd ? "warn" : "debug";

function formatContext(ctx?: Record<string, unknown>): string {
  if (!ctx || Object.keys(ctx).length === 0) return "";
  try {
    return " | " + JSON.stringify(ctx);
  } catch {
    return " | {serialization_error}";
  }
}

export interface ClientLogger {
  debug(message: string, context?: Record<string, unknown>): void;
  info(message: string, context?: Record<string, unknown>): void;
  warn(message: string, context?: Record<string, unknown>): void;
  error(message: string, context?: Record<string, unknown>): void;
  /** Measure duration of an async operation */
  time<T>(label: string, fn: () => Promise<T>, context?: Record<string, unknown>): Promise<T>;
}

/**
 * Create a client-side logger prefixed by module name.
 * 
 * @param module - Module name (e.g., "RealtimeVoice", "StreamingChat")
 * @returns Logger instance with debug/info/warn/error methods
 */
export function createLogger(module: string): ClientLogger {
  function emit(level: LogLevel, message: string, context?: Record<string, unknown>) {
    if (LEVEL_ORDER[level] < LEVEL_ORDER[minLevel]) return;

    const prefix = `[AYU][${module}]`;
    const ctx = formatContext(context);
    const line = `${prefix} ${message}${ctx}`;

    switch (level) {
      case "debug":
        console.debug(line);
        break;
      case "info":
        console.info(line);
        break;
      case "warn":
        console.warn(line);
        break;
      case "error":
        console.error(line);
        break;
    }
  }

  return {
    debug: (msg, ctx?) => emit("debug", msg, ctx),
    info: (msg, ctx?) => emit("info", msg, ctx),
    warn: (msg, ctx?) => emit("warn", msg, ctx),
    error: (msg, ctx?) => emit("error", msg, ctx),

    async time<T>(label: string, fn: () => Promise<T>, context?: Record<string, unknown>): Promise<T> {
      const start = Date.now();
      try {
        const result = await fn();
        const durationMs = Date.now() - start;
        emit("info", `${label} completed`, { ...context, durationMs });
        return result;
      } catch (err: any) {
        const durationMs = Date.now() - start;
        emit("error", `${label} failed`, {
          ...context,
          durationMs,
          error: err?.message || String(err),
        });
        throw err;
      }
    },
  };
}
